/**
 * Reine Ableitung der Info-Zeile des {@link NoteActionsOverlay}: welche Tonhöhe steht über dem
 * Rechtsklick-Menü?
 *
 * Der Kern ist die Unterscheidung der beiden Vorschauen:
 * - **Eingabe (Notenschrift):** notiert == klingend → der Wert kommt direkt aus der `NoteInfo`
 *   (`step`/`alter`/`octave`), ohne Backend.
 * - **Ausgabe (Griffschrift, `preferSounding`):** die notierte Tonhöhe ist die
 *   **Griffschrift-Staff-Position** des Knopfes und gerade **nicht** der erzeugte Ton. Deshalb hier
 *   ausschließlich `sounding_notes` aus `POST /api/note-grips`. Bewusst **kein** Rückfall auf den
 *   notierten Wert: das wäre exakt der irreführende Ton.
 */
import type {NoteInfo} from '@/composables/musicXmlDom';
import {formatPitchDisplay} from '@/composables/musicXmlDom';
import {formatNoteWithOctaveForDisplay} from '@/components/harmonika/noteParse';

export interface NotePitchLabelInput {
    /** Die per Rechtsklick gewählte Note (`null` = keine Auswahl). */
    note: NoteInfo | null | undefined;
    /** true auf der Griffschrift-Ausgabe: nur der klingende Ton ist aussagekräftig. */
    preferSounding: boolean;
    /** `sounding_notes` der note-grips-Antwort (mehrere = Akkord). */
    soundingNotes: string[];
    /** Läuft der Request gerade? Dann Platzhalter statt leerer Zeile. */
    loading: boolean;
}

/** Text der Info-Zeile, oder `null` wenn die Zeile ausgeblendet werden soll. */
export function notePitchLabel({
    note,
    preferSounding,
    soundingNotes,
    loading,
}: NotePitchLabelInput): string | null {
    if (!note) return null;
    if (note.isRest) return 'Pause';

    if (preferSounding) {
        if (soundingNotes.length > 0) {
            return soundingNotes.map(formatNoteWithOctaveForDisplay).join(' + ');
        }
        // Noch unterwegs → Platzhalter; endgültig ohne Griff (unbekannte Staff-Tonhöhe) → keine Zeile.
        return loading ? '…' : null;
    }

    return formatPitchDisplay(note.step, note.alter, note.octave) || null;
}
